import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerOnboardingStageCounts, PartnerOnboardingStatus, withStyles } from 'vopay';
import { BodyText, Card } from 'UI';
import usePartnerAccountStatusLabel from '@/hooks/usePartnerAccountStatusLabel';
import { isPartnerOnboardingStatus } from '@/components/OnboardingTracker/types';
import getVisibleOnboardingStages from '@/components/OnboardingTracker/getVisibleOnboardingStages';
import styles from './OnboardingSummary.module.scss';

const cn = withStyles(styles);

export type OnboardingSummaryProps = {
  /**
   * Entities per onboarding status over the whole book (filters and pagination ignored). Every
   * `PARTNER_ONBOARDING_STATUSES` key is present, zero-filled.
   */
  stageCounts: PartnerOnboardingStageCounts;
  /** The count card's figure ("Active Onboardings"); always the sum of `stageCounts`. */
  totalEntities: number;
  /**
   * `getOnboardingPipeline(partnerTypeLabel, partnerLiability)` narrowed to the onboarding set: the stages
   * always shown, in this order, zero counts greyed. Any other populated status in `stageCounts` follows
   * them (plan step 3).
   */
  pipelineStages: PartnerOnboardingStatus[];
  /** A stage pill was clicked; the page filters the table to that status and returns to page 1. */
  onStageSelect: (status: PartnerOnboardingStatus) => void;
};

type StagePill =
  | { kind: 'known'; status: PartnerOnboardingStatus; count: number }
  | { kind: 'unknown'; rawStatus: string; count: number };

/** Figma 106:23239 (Onboarding Overview → Overview): count card + stage card. Confluence §6.2. */
export default function OnboardingSummary({
  stageCounts,
  totalEntities,
  pipelineStages,
  onStageSelect,
}: OnboardingSummaryProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.OnboardingSummary' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const pills = useMemo((): StagePill[] => {
    const countEntries = Object.entries(stageCounts);

    // The same stages the Status filter offers.
    const knownPills = getVisibleOnboardingStages(pipelineStages, stageCounts).map(
      (status): StagePill => ({ kind: 'known', status, count: stageCounts[status] }),
    );

    // Gap (spec silent): a key outside the onboarding set means the backend added a status before the FE
    // caught up. It still counts towards the total, so it is shown (raw name) when populated, but it
    // can't be selected — the page's status filter only accepts onboarding statuses.
    const unknownPills = countEntries
      .filter(([rawStatus, count]) => !isPartnerOnboardingStatus(rawStatus) && count > 0)
      .map(([rawStatus, count]): StagePill => ({ kind: 'unknown', rawStatus, count }));

    return [...knownPills, ...unknownPills];
  }, [stageCounts, pipelineStages]);

  return (
    <div className={cn('overview')}>
      <Card variant="section" className={cn('card', 'countCard')}>
        {/* Figma `Large Title/40/Emphasized`: no Title level is 40px (level 1 is 32px), so it is styled here. */}
        <span className={cn('totalFigure')} data-testid="onboardingTotalEntities">
          {totalEntities}
        </span>

        <BodyText className={cn('secondary')}>
          {t('activeOnboardings', { defaultValue: 'Active Onboardings' })}
        </BodyText>
      </Card>

      <Card variant="section" className={cn('card', 'stageCard')}>
        <ul className={cn('stageGrid')} aria-label={t('stagesLabel', { defaultValue: 'Onboarding stages' })}>
          {pills.map((pill) =>
            pill.kind === 'known' ? (
              <li key={pill.status}>
                <button
                  type="button"
                  className={cn('stagePill', pill.count === 0 && 'empty')}
                  // Stable hooks for the E2E spec: the greyed state is otherwise only a CSS-module class.
                  data-testid="onboardingStagePill"
                  data-empty={pill.count === 0}
                  onClick={() => onStageSelect(pill.status)}
                >
                  {renderPillContent(resolveStatusLabel(pill.status), pill.count)}
                </button>
              </li>
            ) : (
              <li key={pill.rawStatus} className={cn('stagePill')}>
                {renderPillContent(pill.rawStatus, pill.count)}
              </li>
            ),
          )}
        </ul>
      </Card>
    </div>
  );

  function renderPillContent(label: string, count: number) {
    return (
      <>
        <span className={cn('stageLabel')}>{label}</span>
        <span className={cn('stageCount')}>{count}</span>
      </>
    );
  }
}
